import * as Sentry from "@sentry/react";
import type { AxiosError } from "axios";
import { scrubPayLinkToken } from "@utils/payLink";

// Axios error 分群：預設 Sentry 只依 stack trace 分群，導致所有 axios error
// 無論來源 URL/method 都 merge 成同一個 issue（admin-web prod 曾出現單一 issue
// 5149 hits 實為 7+ 個 endpoint 的混合）。改依 {method, url_template, status} 分群。
const buildAxiosFingerprint = (err: AxiosError) => {
  const req = err.config ?? ({} as NonNullable<AxiosError["config"]>);
  const method = (req.method || "UNKNOWN").toUpperCase();
  const status = err.response?.status ?? "network";
  const urlTemplate = (req.url || "")
    .replace(/\?.*$/, "") // strip query string
    .replace(/\/[A-Z]{1,10}_\d+/g, "/:id") // KQXLO_123 → :id（tenant-prefixed IDs）
    .replace(/\/\d+(?=\/|$)/g, "/:id"); // 純數字 ID
  return ["axios", method, urlTemplate, String(status)];
};

const isAxiosError = (err: unknown): err is AxiosError =>
  !!err &&
  typeof err === "object" &&
  ((err as AxiosError).isAxiosError === true ||
    (err as Error).name === "AxiosError");

const environment = process.env.REACT_APP_SENTRY_ENVIRONMENT;

Sentry.init({
  dsn: process.env.REACT_APP_SENTRY_DSN,
  environment,
  // CI 於 build 時注入（$CI_COMMIT_SHORT_SHA）；配合 sentry-cli 上傳 sourcemap
  // 後，prod 的 stack trace 才能還原成原始碼。本機開發不設 → undefined，Sentry 忽略。
  release: process.env.REACT_APP_SENTRY_RELEASE,
  // prod 全量 tracing 會燒配額；錯誤事件（captureException）不受此值影響。
  tracesSampleRate: environment === "prod" ? 0.05 : 1.0,
  beforeSend(event, hint) {
    const err = hint?.originalException;
    if (isAxiosError(err)) {
      event.fingerprint = buildAxiosFingerprint(err);
      event.tags = {
        ...(event.tags || {}),
        axios_method: (err.config?.method || "unknown").toUpperCase(),
        axios_status: String(err.response?.status ?? "network"),
      };
    }

    // 一次性付款連結（/pay/:orderId?pt=<token>）的 pt 不可外洩到 Sentry：清掉事件本身的
    // request URL，以及 breadcrumb（navigation 的 from/to、fetch/xhr 的 url）裡殘留的 pt 值。
    // PayLinkView 讀完會 replaceState 清網址列，但那之前送出的 breadcrumb／事件已經記錄下來了。
    if (event.request?.url) {
      event.request.url = scrubPayLinkToken(event.request.url);
    }
    if (event.breadcrumbs) {
      event.breadcrumbs = event.breadcrumbs.map((crumb) => {
        if (!crumb.data) return crumb;
        const data = { ...crumb.data };
        if (typeof data.from === "string") data.from = scrubPayLinkToken(data.from);
        if (typeof data.to === "string") data.to = scrubPayLinkToken(data.to);
        if (typeof data.url === "string") data.url = scrubPayLinkToken(data.url);
        return { ...crumb, data };
      });
    }

    return event;
  },
});
